import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { MedalChip } from "../../components/medals/medal-chip";
import { Card } from "../../components/page/card";
import { PageHeader } from "../../components/page/page-header";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { medalListQuery, tallyQuery } from "../../lib/desk/queries";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatDateTime, formatInteger, formatShort } from "../../lib/i18n/format";

const medalsSearch = z.object({
	sport: z.string().uuid().optional().catch(undefined),
	noc: z.string().uuid().optional().catch(undefined),
});

/**
 * Every awarded medal of the games, newest first — `GET /medals`, a keyset
 * page per "Show more" (no page count: a keyset list has no total by design).
 * Filters: sport (`/sports/options`) and NOC (the tally's organisations: one
 * bounded read the tally page shares). Only `event_medals` rows ever show
 * here: OVR's suggestions never do.
 */
export const Route = createFileRoute("/_app/medals")({
	validateSearch: medalsSearch,
	component: MedalsPage,
});

const SELECT = "min-h-touch w-full rounded-btn border border-rule bg-sheet px-[0.625rem] text-[0.875rem] text-ink focus:border-board sm:w-[16rem]";

function MedalsPage() {
	const { t } = useTranslation("medals");
	const tc = useTranslation().t;
	const { sport, noc } = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const { gamesId, games, isLoading: gamesLoading } = useCurrentGames();
	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });
	const tally = useQuery({ ...tallyQuery(gamesId ?? ""), enabled: gamesId !== null });
	const list = useInfiniteQuery({
		...medalListQuery({ games_id: gamesId ?? "", sport_id: sport, responsible_organization_id: noc }),
		enabled: gamesId !== null,
	});
	const items = list.data?.pages.flatMap((p) => p.items) ?? [];

	if (!gamesId) {
		return gamesLoading ? <Spinner className="size-[1.5rem] text-board" label={tc("state.loading")} /> : <EmptyState title={t("noGames.title")} body={t("noGames.body")} />;
	}

	return (
		<>
			<PageHeader title={t("list.title")} subtitle={t("list.subtitle", { games: games?.name ?? "" })} />
			<div className="mb-[1rem] flex flex-wrap items-end gap-[0.75rem]">
				<label className="flex flex-col gap-[0.25rem] text-[0.8125rem] font-bold text-muted">
					{t("filters.sport")}
					<select className={SELECT} value={sport ?? ""} onChange={(e) => void navigate({ search: (s) => ({ ...s, sport: e.target.value || undefined }) })}>
						<option value="">{t("filters.allSports")}</option>
						{(sports.data ?? []).map((s) => (
							<option key={s.sport_id} value={s.sport_id}>
								{s.name}
							</option>
						))}
					</select>
				</label>
				<label className="flex flex-col gap-[0.25rem] text-[0.8125rem] font-bold text-muted">
					{t("filters.noc")}
					<select className={SELECT} value={noc ?? ""} onChange={(e) => void navigate({ search: (s) => ({ ...s, noc: e.target.value || undefined }) })}>
						<option value="">{t("filters.allNocs")}</option>
						{(tally.data?.rows ?? []).map((r) => (
							<option key={r.noc.responsible_organization_id} value={r.noc.responsible_organization_id}>
								{r.noc.code ? `${r.noc.code} · ${r.noc.name}` : r.noc.name}
							</option>
						))}
					</select>
				</label>
			</div>

			<Card className="overflow-hidden">
				{list.isPending ? (
					<div className="p-[1rem]">
						<Spinner className="size-[1.25rem] text-board" label={tc("state.loading")} />
					</div>
				) : list.isError ? (
					<p className="p-[1rem] text-conflict">{errorMessage(list.error)}</p>
				) : items.length === 0 ? (
					<p className="p-[1rem] text-[0.875rem] text-muted">{sport || noc ? t("list.emptyFiltered") : t("list.empty")}</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[44rem] text-left text-[0.875rem]">
							<thead className="border-rule border-b bg-paper text-[0.75rem] text-muted uppercase tracking-wide">
								<tr>
									<th className="px-[1rem] py-[0.5rem]">{t("list.medal")}</th>
									<th className="px-[1rem] py-[0.5rem]">{t("list.event")}</th>
									<th className="px-[1rem] py-[0.5rem]">{t("list.competitor")}</th>
									<th className="px-[1rem] py-[0.5rem]">{t("list.awarded")}</th>
								</tr>
							</thead>
							<tbody>
								{items.map((m) => (
									<tr key={m.event_medal_id} className="border-rule border-b last:border-b-0">
										<td className="px-[1rem] py-[0.5rem]">
											<MedalChip medal={m.medal} />
										</td>
										<td className="px-[1rem] py-[0.5rem]">
											<span className="block font-bold">{m.event.name}</span>
											<span className="text-[0.75rem] text-muted">
												{m.sport.name} · <span className="font-mono">{m.event.code}</span>
											</span>
										</td>
										<td className="px-[1rem] py-[0.5rem]">
											<span className="block">{m.competitor.name}</span>
											<span className="font-mono text-[0.75rem] text-muted">{m.competitor.noc.code ?? m.competitor.noc.name}</span>
										</td>
										<td className="whitespace-nowrap px-[1rem] py-[0.5rem]">
											<time dateTime={m.awarded_at} title={formatDateTime(m.awarded_at)}>
												{formatShort(m.awarded_at)}
											</time>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{items.length > 0 && (
					<div className="flex flex-wrap items-center gap-[0.75rem] border-rule border-t px-[1rem] py-[0.5rem] text-[0.8125rem] text-muted">
						<span>{t("list.showing", { count: items.length, n: formatInteger(items.length) })}</span>
						{list.hasNextPage ? (
							<Button size="sm" variant="outline" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
								{tc("actions.showMore")}
							</Button>
						) : (
							<span>{t("list.allLoaded")}</span>
						)}
					</div>
				)}
			</Card>
		</>
	);
}
